import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import clinicalNoteService from './clinicalNoteService.js';

/**
 * Convert backend enum-style values into readable text.
 *
 * Examples:
 *
 * READY_FOR_REVIEW -> Ready For Review
 * REPETITION       -> Repetition
 * NOT_APPLICABLE   -> Not Applicable
 */
function formatEnumLabel(value) {
  if (!value) {
    return '';
  }

  return String(value)
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map(
      (part) =>
        part.charAt(0).toUpperCase() +
        part.slice(1)
    )
    .join(' ');
}

/**
 * Convert a backend clinical section code into
 * a user-friendly section name.
 */
function formatSectionName(sectionCode) {
  switch (sectionCode) {
    case 'PAIN':
      return 'Pain';

    case 'ROM':
      return 'Range of Motion';

    case 'STRENGTH':
      return 'Strength';

    case 'GOAL':
      return 'Goals';

    default:
      return (
        formatEnumLabel(sectionCode) ||
        'Clinical Warning'
      );
  }
}

/**
 * Create a stable frontend key for one warning.
 *
 * A single ROM or Strength record can potentially carry
 * more than one warning type, so warningCode must be
 * included in the key.
 */
function getWarningKey(warning) {
  return [
    warning?.sectionCode || 'section',
    warning?.warningRecordId || 'record',
    warning?.warningCode || 'warning',
  ].join('|');
}

/**
 * Determine whether one warning still requires
 * acknowledgement.
 */
function isWarningUnresolved(warning) {
  return (
    warning?.unresolved === true &&
    warning?.warningAcknowledged !== true
  );
}

/**
 * Display one clinical warning.
 */
function WarningCard({
  warning,
  reason,
  disabled,
  submitting,
  error,
  onReasonChange,
  onAcknowledge,
}) {
  const unresolved =
    isWarningUnresolved(warning);

  const acknowledged =
    warning?.warningAcknowledged === true;

  const reasonLength =
    reason?.length || 0;

  return (
    <article
      style={{
        border: unresolved
          ? '1px solid #d6a100'
          : '1px solid #a8d5b0',
        background: unresolved
          ? '#fffaf0'
          : '#f5fbf6',
        borderRadius: 9,
        overflow: 'hidden',
      }}
    >
      {/* =====================================================
          WARNING HEADER
          ===================================================== */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 10,
          flexWrap: 'wrap',
          padding: '10px 12px',
          borderBottom: '1px solid #e1e1e1',
        }}
      >
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              flexWrap: 'wrap',
            }}
          >
            <strong
              style={{
                fontSize: 14,
              }}
            >
              {warning.fieldDisplayName ||
                formatSectionName(
                  warning.sectionCode
                )}
            </strong>

            {warning.measurementSide && (
              <span
                style={{
                  borderRadius: 999,
                  padding: '2px 7px',
                  background: '#eef1f4',
                  fontSize: 10,
                  fontWeight: 700,
                }}
              >
                {formatEnumLabel(
                  warning.measurementSide
                )}
              </span>
            )}

            {warning.warningCode && (
              <span
                style={{
                  borderRadius: 999,
                  padding: '2px 7px',
                  background: unresolved
                    ? '#fff0b3'
                    : '#e4f2e7',
                  color: unresolved
                    ? '#795500'
                    : '#276738',
                  fontSize: 10,
                  fontWeight: 700,
                }}
              >
                {formatEnumLabel(
                  warning.warningCode
                )}
              </span>
            )}
          </div>

          <div
            style={{
              marginTop: 4,
              fontSize: 11,
              color: '#777',
            }}
          >
            {formatSectionName(
              warning.sectionCode
            )}
          </div>
        </div>

        <div
          style={{
            borderRadius: 999,
            padding: '3px 8px',
            background: unresolved
              ? '#fff3cd'
              : '#e7f6ea',
            color: unresolved
              ? '#795500'
              : '#276738',
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {unresolved
            ? 'Needs Review'
            : 'Acknowledged'}
        </div>
      </div>

      <div
        style={{
          padding: 12,
        }}
      >
        {/* ===================================================
            BACKEND WARNING MESSAGE
            =================================================== */}
        <div
          style={{
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          {warning.warningMessage ||
            'Clinical documentation warning detected.'}
        </div>

        {/* ===================================================
            VALUE COMPARISON
            =================================================== */}
        {(warning.previousValue !== null &&
          warning.previousValue !== undefined) ||
        (warning.currentValue !== null &&
          warning.currentValue !== undefined) ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns:
                'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 8,
              marginTop: 10,
            }}
          >
            {warning.previousValue !== null &&
              warning.previousValue !==
                undefined && (
                <div
                  style={{
                    border:
                      '1px solid #ddd',
                    borderRadius: 7,
                    padding: 8,
                    background: '#fff',
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      color: '#777',
                      fontWeight: 700,
                      textTransform:
                        'uppercase',
                    }}
                  >
                    Previous
                  </div>

                  <div
                    style={{
                      marginTop: 3,
                      fontWeight: 700,
                    }}
                  >
                    {String(
                      warning.previousValue
                    )}
                  </div>
                </div>
              )}

            {warning.currentValue !== null &&
              warning.currentValue !==
                undefined && (
                <div
                  style={{
                    border:
                      '1px solid #ddd',
                    borderRadius: 7,
                    padding: 8,
                    background: '#fff',
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      color: '#777',
                      fontWeight: 700,
                      textTransform:
                        'uppercase',
                    }}
                  >
                    Current
                  </div>

                  <div
                    style={{
                      marginTop: 3,
                      fontWeight: 700,
                    }}
                  >
                    {String(
                      warning.currentValue
                    )}
                  </div>
                </div>
              )}
          </div>
        ) : null}

        {/* ===================================================
            ACKNOWLEDGED HISTORY
            =================================================== */}
        {acknowledged && (
          <div
            style={{
              marginTop: 11,
              border:
                '1px solid #b8d9bf',
              borderRadius: 7,
              background: '#f4fbf5',
              padding: 10,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: '#276738',
                textTransform: 'uppercase',
              }}
            >
              Clinical Explanation
            </div>

            <div
              style={{
                marginTop: 5,
                fontSize: 13,
                lineHeight: 1.45,
                whiteSpace: 'pre-wrap',
              }}
            >
              {warning.warningReason ||
                'Warning acknowledged.'}
            </div>
          </div>
        )}

        {/* ===================================================
            ACKNOWLEDGEMENT FORM
            =================================================== */}
        {unresolved && (
          <div
            style={{
              marginTop: 12,
              borderTop:
                '1px solid #e1e1e1',
              paddingTop: 11,
            }}
          >
            <label
              style={{
                display: 'block',
                fontSize: 12,
                fontWeight: 700,
                marginBottom: 5,
              }}
            >
              Clinical explanation
            </label>

            <textarea
              value={reason}
              disabled={
                disabled ||
                submitting
              }
              maxLength={2000}
              rows={4}
              placeholder="Explain why the documented clinical value is correct and should be retained."
              onChange={(event) =>
                onReasonChange(
                  event.target.value
                )
              }
              style={{
                width: '100%',
                boxSizing:
                  'border-box',
                resize: 'vertical',
                minHeight: 90,
                padding: 9,
                fontFamily: 'inherit',
                fontSize: 13,
                lineHeight: 1.45,
              }}
            />

            <div
              style={{
                display: 'flex',
                justifyContent:
                  'space-between',
                gap: 10,
                alignItems: 'center',
                flexWrap: 'wrap',
                marginTop: 5,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  color:
                    reasonLength >= 1900
                      ? '#8a6500'
                      : '#777',
                }}
              >
                {reasonLength}/2000
              </span>

              <button
                type="button"
                disabled={
                  disabled ||
                  submitting ||
                  !reason?.trim()
                }
                onClick={
                  onAcknowledge
                }
              >
                {submitting
                  ? 'Acknowledging…'
                  : 'Acknowledge Warning'}
              </button>
            </div>

            {error && (
              <div
                role="alert"
                style={{
                  marginTop: 8,
                  border:
                    '1px solid #b42318',
                  borderRadius: 6,
                  background:
                    '#fff5f5',
                  padding: 8,
                  fontSize: 12,
                  color: '#8a1c13',
                }}
              >
                {error}
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

/**
 * Clinical warning acknowledgement panel.
 *
 * Important rules:
 *
 * - warnings are created only by Spring Boot
 * - frontend never creates or removes warning flags
 * - acknowledgement requires a clinical explanation
 * - acknowledgement uses optimistic locking
 * - acknowledged warnings remain visible as history
 * - one acknowledgement request is processed at a time
 */
export default function ClinicalNoteWarningPanel({
  clinicalNoteId,
  enabled = true,
  disabled = false,
  refreshToken = 0,
  onAcknowledged,
  onVersionChange,
}) {
  const [
    warnings,
    setWarnings,
  ] = useState([]);

  const [
    entityVersion,
    setEntityVersion,
  ] = useState(null);

  const [
    reasons,
    setReasons,
  ] = useState({});

  const [
    submittingKey,
    setSubmittingKey,
  ] = useState(null);

  const [
    warningErrors,
    setWarningErrors,
  ] = useState({});

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    loadError,
    setLoadError,
  ] = useState('');

  /**
   * Load both:
   *
   * - warning history
   * - current parent note version
   *
   * Warning responses intentionally do not carry the parent
   * ClinicalNoteMain entityVersion, so the current note is
   * loaded alongside the warnings.
   */
  const loadWarnings =
    useCallback(
      async () => {
        if (
          !enabled ||
          !clinicalNoteId
        ) {
          return;
        }

        setLoading(true);
        setLoadError('');

        try {
          const [
            warningResponse,
            noteResponse,
          ] = await Promise.all([
            clinicalNoteService
              .getWarnings(
                clinicalNoteId
              ),

            clinicalNoteService
              .getClinicalNote(
                clinicalNoteId
              ),
          ]);

          setWarnings(
            Array.isArray(
              warningResponse
            )
              ? warningResponse
              : []
          );

          const nextVersion =
            noteResponse?.entityVersion;

          if (
            nextVersion !== null &&
            nextVersion !== undefined
          ) {
            setEntityVersion(
              nextVersion
            );

            if (
              typeof onVersionChange ===
              'function'
            ) {
              onVersionChange(
                nextVersion,
                noteResponse
              );
            }
          }
        } catch (error) {
          setLoadError(
            error?.message ||
              'Clinical warnings could not be loaded.'
          );
        } finally {
          setLoading(false);
        }
      },
      [
        clinicalNoteId,
        enabled,
        onVersionChange,
      ]
    );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    loadWarnings();
  }, [
    loadWarnings,
    refreshToken,
    enabled,
  ]);

  /**
   * Keep unresolved warnings first.
   *
   * Within each state, organize warnings by clinical section,
   * field, side, and warning type.
   */
  const sortedWarnings =
    useMemo(() => {
      return [...warnings].sort(
        (first, second) => {
          const firstUnresolved =
            isWarningUnresolved(
              first
            );

          const secondUnresolved =
            isWarningUnresolved(
              second
            );

          if (
            firstUnresolved !==
            secondUnresolved
          ) {
            return firstUnresolved
              ? -1
              : 1;
          }

          const firstSection =
            first.sectionCode || '';

          const secondSection =
            second.sectionCode || '';

          const sectionCompare =
            firstSection.localeCompare(
              secondSection
            );

          if (
            sectionCompare !== 0
          ) {
            return sectionCompare;
          }

          const firstName =
            first.fieldDisplayName ||
            '';

          const secondName =
            second.fieldDisplayName ||
            '';

          const nameCompare =
            firstName.localeCompare(
              secondName
            );

          if (nameCompare !== 0) {
            return nameCompare;
          }

          const firstSide =
            first.measurementSide ||
            '';

          const secondSide =
            second.measurementSide ||
            '';

          const sideCompare =
            firstSide.localeCompare(
              secondSide
            );

          if (sideCompare !== 0) {
            return sideCompare;
          }

          return String(
            first.warningCode || ''
          ).localeCompare(
            String(
              second.warningCode || ''
            )
          );
        }
      );
    }, [warnings]);

  const unresolvedCount =
    useMemo(
      () =>
        warnings.filter(
          isWarningUnresolved
        ).length,
      [warnings]
    );

  const acknowledgedCount =
    warnings.length -
    unresolvedCount;

  /**
   * Update the local explanation for one warning.
   */
  function updateReason(
    warning,
    value
  ) {
    const key =
      getWarningKey(
        warning
      );

    setReasons(
      (current) => ({
        ...current,
        [key]: value,
      })
    );

    setWarningErrors(
      (current) => {
        if (!current[key]) {
          return current;
        }

        const next = {
          ...current,
        };

        delete next[key];

        return next;
      }
    );
  }

  /**
   * Reload only the parent note version.
   *
   * This is useful after an optimistic-lock conflict so the
   * user's typed acknowledgement reason can remain on screen
   * while the component obtains the latest note version.
   */
  async function refreshVersionAfterFailure() {
    try {
      const note =
        await clinicalNoteService
          .getClinicalNote(
            clinicalNoteId
          );

      if (
        note?.entityVersion !==
          null &&
        note?.entityVersion !==
          undefined
      ) {
        setEntityVersion(
          note.entityVersion
        );

        if (
          typeof onVersionChange ===
          'function'
        ) {
          onVersionChange(
            note.entityVersion,
            note
          );
        }
      }
    } catch {
      /*
       * Preserve the original acknowledgement error.
       *
       * A secondary version-refresh failure should not replace
       * the more useful error from the acknowledgement request.
       */
    }
  }

  /**
   * Acknowledge one existing backend-generated warning.
   */
  async function acknowledgeWarning(
    warning
  ) {
    if (
      disabled ||
      submittingKey !== null ||
      !isWarningUnresolved(
        warning
      )
    ) {
      return;
    }

    const key =
      getWarningKey(
        warning
      );

    const reason =
      reasons[key]?.trim() ||
      '';

    if (!reason) {
      setWarningErrors(
        (current) => ({
          ...current,
          [key]:
            'A clinical explanation is required before acknowledging this warning.',
        })
      );

      return;
    }

    if (
      reason.length > 2000
    ) {
      setWarningErrors(
        (current) => ({
          ...current,
          [key]:
            'The acknowledgement explanation cannot exceed 2000 characters.',
        })
      );

      return;
    }

    if (
      entityVersion === null ||
      entityVersion ===
        undefined
    ) {
      setWarningErrors(
        (current) => ({
          ...current,
          [key]:
            'The current clinical note version is unavailable. Refresh the warnings and try again.',
        })
      );

      return;
    }

    setSubmittingKey(
      key
    );

    setWarningErrors(
      (current) => {
        const next = {
          ...current,
        };

        delete next[key];

        return next;
      }
    );

    try {
      const response =
        await clinicalNoteService
          .acknowledgeWarning(
            clinicalNoteId,
            {
              sectionCode:
                warning.sectionCode,

              warningRecordId:
                warning.warningRecordId,

              warningCode:
                warning.warningCode,

              warningReason:
                reason,

              expectedVersion:
                entityVersion,
            }
          );

      /*
       * Reload the complete warning list instead of replacing
       * only the clicked warning.
       *
       * This is important because the backend is authoritative
       * for warning state and recalculates the note-wide warning
       * summary after acknowledgement.
       */
      const [
        refreshedWarnings,
        refreshedNote,
      ] = await Promise.all([
        clinicalNoteService
          .getWarnings(
            clinicalNoteId
          ),

        clinicalNoteService
          .getClinicalNote(
            clinicalNoteId
          ),
      ]);

      setWarnings(
        Array.isArray(
          refreshedWarnings
        )
          ? refreshedWarnings
          : []
      );

      if (
        refreshedNote
          ?.entityVersion !==
          null &&
        refreshedNote
          ?.entityVersion !==
          undefined
      ) {
        setEntityVersion(
          refreshedNote.entityVersion
        );

        if (
          typeof onVersionChange ===
          'function'
        ) {
          onVersionChange(
            refreshedNote.entityVersion,
            refreshedNote
          );
        }
      }

      setReasons(
        (current) => {
          const next = {
            ...current,
          };

          delete next[key];

          return next;
        }
      );

      if (
        typeof onAcknowledged ===
        'function'
      ) {
        onAcknowledged(
          response,
          refreshedWarnings,
          refreshedNote
        );
      }
    } catch (error) {
      setWarningErrors(
        (current) => ({
          ...current,
          [key]:
            error?.message ||
            'The clinical warning could not be acknowledged.',
        })
      );

      /*
       * Another autosave may have advanced the parent note's
       * optimistic-lock version.
       *
       * Refreshing only the version here preserves the user's
       * explanation for an immediate retry.
       */
      await refreshVersionAfterFailure();
    } finally {
      setSubmittingKey(
        null
      );
    }
  }

  if (!enabled) {
    return null;
  }

  return (
    <section
      style={{
        border:
          '1px solid #d7d7d7',
        borderRadius: 10,
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* =====================================================
          HEADER
          ===================================================== */}
      <div
        style={{
          display: 'flex',
          justifyContent:
            'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          padding:
            '13px 16px',
          background:
            '#f7f8fa',
          borderBottom:
            '1px solid #ddd',
        }}
      >
        <div>
          <h3
            style={{
              margin: 0,
              fontSize: 17,
            }}
          >
            Clinical Warnings
          </h3>

          <div
            style={{
              marginTop: 3,
              fontSize: 12,
              color: '#666',
            }}
          >
            Review backend-generated
            clinical warnings before
            completing the note.
          </div>
        </div>

        <button
          type="button"
          disabled={
            loading ||
            submittingKey !== null
          }
          onClick={
            loadWarnings
          }
        >
          {loading
            ? 'Refreshing…'
            : 'Refresh Warnings'}
        </button>
      </div>

      <div
        style={{
          padding: 16,
        }}
      >
        {/* ===================================================
            LOAD ERROR
            =================================================== */}
        {loadError && (
          <div
            role="alert"
            style={{
              marginBottom: 12,
              border:
                '1px solid #b42318',
              borderRadius: 8,
              background:
                '#fff5f5',
              padding: 10,
            }}
          >
            <strong>
              Unable to load clinical
              warnings.
            </strong>

            <div
              style={{
                marginTop: 4,
                fontSize: 13,
              }}
            >
              {loadError}
            </div>
          </div>
        )}

        {/* ===================================================
            SUMMARY
            =================================================== */}
        {!loadError && (
          <div
            style={{
              display: 'flex',
              gap: 7,
              flexWrap: 'wrap',
              marginBottom: 13,
            }}
          >
            <span
              style={{
                borderRadius: 999,
                padding:
                  '4px 9px',
                background:
                  unresolvedCount > 0
                    ? '#fff3cd'
                    : '#e7f6ea',
                color:
                  unresolvedCount > 0
                    ? '#795500'
                    : '#276738',
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {unresolvedCount}{' '}
              unresolved{' '}
              {unresolvedCount === 1
                ? 'warning'
                : 'warnings'}
            </span>

            {acknowledgedCount >
              0 && (
              <span
                style={{
                  borderRadius: 999,
                  padding:
                    '4px 9px',
                  background:
                    '#e7f6ea',
                  color: '#276738',
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {
                  acknowledgedCount
                }{' '}
                acknowledged
              </span>
            )}
          </div>
        )}

        {/* ===================================================
            LOADING
            =================================================== */}
        {loading &&
          warnings.length ===
            0 &&
          !loadError && (
            <div
              style={{
                fontSize: 13,
                color: '#666',
              }}
            >
              Loading clinical
              warnings…
            </div>
          )}

        {/* ===================================================
            NO WARNINGS
            =================================================== */}
        {!loading &&
          !loadError &&
          sortedWarnings.length ===
            0 && (
            <div
              style={{
                border:
                  '1px solid #a8d5b0',
                borderRadius: 8,
                background:
                  '#f4fbf5',
                padding: 12,
                color: '#276738',
                fontWeight: 700,
              }}
            >
              No clinical warnings are
              currently present.
            </div>
          )}

        {/* ===================================================
            WARNING LIST
            =================================================== */}
        {sortedWarnings.length >
          0 && (
          <div
            style={{
              display: 'grid',
              gap: 11,
            }}
          >
            {sortedWarnings.map(
              (warning) => {
                const key =
                  getWarningKey(
                    warning
                  );

                return (
                  <WarningCard
                    key={key}
                    warning={
                      warning
                    }
                    reason={
                      reasons[key] ||
                      ''
                    }
                    disabled={
                      disabled ||
                      submittingKey !==
                        null
                    }
                    submitting={
                      submittingKey ===
                      key
                    }
                    error={
                      warningErrors[
                        key
                      ] || ''
                    }
                    onReasonChange={(
                      value
                    ) =>
                      updateReason(
                        warning,
                        value
                      )
                    }
                    onAcknowledge={() =>
                      acknowledgeWarning(
                        warning
                      )
                    }
                  />
                );
              }
            )}
          </div>
        )}

        {entityVersion !==
          null &&
          entityVersion !==
            undefined && (
          <div
            style={{
              marginTop: 12,
              textAlign: 'right',
              fontSize: 10,
              color: '#999',
            }}
          >
            Clinical note version{' '}
            {entityVersion}
          </div>
        )}
      </div>
    </section>
  );
}